/**
 * Database Backend
 * 
 * Repository API for ShowGroom using IndexedDB via Dexie.
 * Provides a compatible interface with the old localStorage backend.
 * Includes one-time migration from localStorage.
 */

import { exportDB, importInto } from 'dexie-export-import';
import {
  db,
  generateId,
  hashPassword,
  initializeDatabase,
  DEFAULT_BUSINESS_HOURS,
  DEFAULT_ADMIN,
} from '../db/showgroom-db';

// Re-export types for use in components
export type {
  User,
  Booking,
  GalleryImage,
  ImagePair,
  ImageSEOData,
  BusinessHoursData,
  Role,
  BookingStatus,
} from '../db/showgroom-db';

import type {
  User,
  Booking,
  GalleryImage,
  ImageSEOData,
  BusinessHoursData,
  Role,
  BookingStatus,
} from '../db/showgroom-db';

// ==================== Storage Keys (for migration) ====================

const LEGACY_STORAGE_KEYS = {
  IMAGES: 'showgroom_images',
  BOOKINGS: 'showgroom_bookings',
  BUSINESS_HOURS: 'showgroom_business_hours',
  USERS: 'showgroom_users',
  AUTH_TOKEN: 'showgroom_auth_token',
  CURRENT_USER: 'showgroom_current_user',
};

// ==================== Session Storage ====================

let currentToken: string | null = null;
let currentUser: User | null = null;

// ==================== Migration ====================

async function migrateFromLocalStorage(): Promise<boolean> {
  // Check if migration already done
  const settings = await db.settings.get('singleton');
  if (settings?.migratedFromLocalStorage) {
    return false;
  }

  let migrated = false;

  // Migrate users
  const usersJson = localStorage.getItem(LEGACY_STORAGE_KEYS.USERS);
  if (usersJson) {
    try {
      const users = JSON.parse(usersJson);
      if (Array.isArray(users) && users.length > 0) {
        const existingCount = await db.users.count();
        if (existingCount === 0) {
          await db.users.bulkAdd(users);
          migrated = true;
        }
      }
    } catch (e) {
      // Failed to migrate users
    }
  }

  // Migrate bookings
  const bookingsJson = localStorage.getItem(LEGACY_STORAGE_KEYS.BOOKINGS);
  if (bookingsJson) {
    try {
      const bookings = JSON.parse(bookingsJson);
      if (Array.isArray(bookings) && bookings.length > 0) {
        const existingCount = await db.bookings.count();
        if (existingCount === 0) {
          await db.bookings.bulkAdd(bookings);
          migrated = true;
        }
      }
    } catch (e) {
      // Failed to migrate bookings
    }
  }

  // Migrate business hours
  const hoursJson = localStorage.getItem(LEGACY_STORAGE_KEYS.BUSINESS_HOURS);
  if (hoursJson) {
    try {
      const hours = JSON.parse(hoursJson);
      if (hours && typeof hours === 'object') {
        await db.businessHours.put({
          id: 'singleton',
          data: hours,
        });
        migrated = true;
      }
    } catch (e) {
      // Failed to migrate business hours
    }
  }

  // Migrate images (URLs only - no blobs in old format)
  const imagesJson = localStorage.getItem(LEGACY_STORAGE_KEYS.IMAGES);
  if (imagesJson) {
    try {
      const images = JSON.parse(imagesJson);
      if (Array.isArray(images) && images.length > 0) {
        const existingCount = await db.images.count();
        if (existingCount === 0) {
          // Convert old format: prefer afterUrl (finished result), fall back to beforeUrl
          const mappedImages: GalleryImage[] = images.map((img: any) => ({
            id: img.id,
            imageUrl: img.afterUrl || img.beforeUrl || img.imageUrl,
            uploadedAt: img.uploadedAt,
          }));
          await db.images.bulkAdd(mappedImages);
          migrated = true;
        }
      }
    } catch (e) {
      // Failed to migrate images
    }
  }

  // Mark migration complete
  await db.settings.put({
    id: 'singleton',
    migratedFromLocalStorage: true,
  });

  if (migrated) {
    // Optionally clear old localStorage keys
    // Object.values(LEGACY_STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  }

  return migrated;
}

// ==================== Initialization ====================

let initPromise: Promise<void> | null = null;

export async function ensureInitialized(): Promise<void> {
  if (!initPromise) {
    initPromise = (async () => {
      await initializeDatabase();
      await migrateFromLocalStorage();
    })();
  }
  return initPromise;
}

// ==================== AUTH ====================

export const dbAuth = {
  getAllUsers: async (): Promise<User[]> => {
    await ensureInitialized();
    return db.users.toArray();
  },

  hasAdmin: async (): Promise<boolean> => {
    await ensureInitialized();
    const count = await db.users.count();
    return count > 0;
  },

  addUser: async (
    username: string,
    email: string,
    password: string,
    role: Role = 'user'
  ): Promise<User> => {
    await ensureInitialized();

    // Check if username or email already exists
    const existingByUsername = await db.users.where('username').equals(username).first();
    if (existingByUsername) {
      throw new Error('Username already exists');
    }

    const existingByEmail = await db.users.where('email').equals(email).first();
    if (existingByEmail) {
      throw new Error('Email already exists');
    }

    const passwordHash = await hashPassword(password);
    const newUser: User = {
      id: generateId(),
      username,
      email,
      passwordHash,
      role,
      createdAt: new Date().toISOString(),
    };

    await db.users.add(newUser);
    return newUser;
  },

  deleteUser: async (userId: string): Promise<void> => {
    await ensureInitialized();

    const user = await db.users.get(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Check if this is the last admin
    const admins = await db.users.where('role').equals('admin').toArray();
    if (user.role === 'admin' && admins.length === 1) {
      throw new Error('Cannot delete the last admin user');
    }

    await db.users.delete(userId);
  },

  updateUser: async (
    userId: string,
    updates: Partial<Omit<User, 'id' | 'passwordHash'>>
  ): Promise<User> => {
    await ensureInitialized();

    const user = await db.users.get(userId);
    if (!user) {
      throw new Error('User not found');
    }

    await db.users.update(userId, updates);
    return { ...user, ...updates };
  },

  changePassword: async (
    userId: string,
    oldPassword: string,
    newPassword: string
  ): Promise<void> => {
    await ensureInitialized();

    const user = await db.users.get(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Verify old password
    const oldHash = await hashPassword(oldPassword);
    if (user.passwordHash !== oldHash) {
      throw new Error('Current password is incorrect');
    }

    // Set new password
    const newHash = await hashPassword(newPassword);
    await db.users.update(userId, { passwordHash: newHash });
  },

  login: async (usernameOrEmail: string, password: string): Promise<string> => {
    await ensureInitialized();

    const passwordHash = await hashPassword(password);

    // Find user by username or email
    let user = await db.users.where('username').equals(usernameOrEmail).first();
    if (!user) {
      user = await db.users.where('email').equals(usernameOrEmail).first();
    }

    if (!user || user.passwordHash !== passwordHash) {
      throw new Error('Invalid credentials');
    }

    // Generate token
    const token = btoa(
      JSON.stringify({
        userId: user.id,
        username: user.username,
        role: user.role,
        timestamp: Date.now(),
        random: Math.random(),
      })
    );

    currentToken = token;
    currentUser = user;

    // Store in session storage for tab persistence
    sessionStorage.setItem('showgroom_token', token);
    sessionStorage.setItem('showgroom_user', JSON.stringify(user));

    return token;
  },

  verify: async (token: string): Promise<boolean> => {
    if (currentToken === token) {
      return true;
    }
    
    // Check session storage
    const storedToken = sessionStorage.getItem('showgroom_token');
    if (storedToken === token) {
      currentToken = token;
      const userJson = sessionStorage.getItem('showgroom_user');
      if (userJson) {
        currentUser = JSON.parse(userJson);
      }
      return true;
    }
    
    return false;
  },

  getCurrentUser: (): User | null => {
    if (currentUser) return currentUser;
    
    // Try session storage
    const userJson = sessionStorage.getItem('showgroom_user');
    if (userJson) {
      currentUser = JSON.parse(userJson);
      return currentUser;
    }
    
    return null;
  },

  getToken: (): string | null => {
    if (currentToken) return currentToken;
    return sessionStorage.getItem('showgroom_token');
  },

  logout: async (): Promise<void> => {
    currentToken = null;
    currentUser = null;
    sessionStorage.removeItem('showgroom_token');
    sessionStorage.removeItem('showgroom_user');
  },

  resetAuth: async (): Promise<void> => {
    await db.users.clear();
    await db.users.add(DEFAULT_ADMIN);
    currentToken = null;
    currentUser = null;
    sessionStorage.removeItem('showgroom_token');
    sessionStorage.removeItem('showgroom_user');
  },
};

// ==================== IMAGES ====================

export const dbImages = {
  getAll: async (): Promise<GalleryImage[]> => {
    await ensureInitialized();
    const images = await db.images.toArray();
    return images;
  },

  add: async (imageData: Blob | string): Promise<GalleryImage> => {
    await ensureInitialized();

    const newImage: GalleryImage = {
      id: generateId(),
      uploadedAt: new Date().toISOString(),
    };

    if (imageData instanceof Blob) {
      newImage.imageBlob = imageData;
    } else {
      newImage.imageUrl = imageData;
    }

    await db.images.add(newImage);
    return newImage;
  },

  delete: async (id: string): Promise<void> => {
    await ensureInitialized();
    await db.images.delete(id);
  },

  clear: async (): Promise<void> => {
    await db.images.clear();
  },

  // Helper to get URL for an image (handles Blob conversion)
  // WARNING: This creates a new blob URL each time - use getImageDataUrl for persistent URLs
  getImageUrl: (image: GalleryImage): string | null => {
    if (image.imageBlob) {
      return URL.createObjectURL(image.imageBlob);
    }
    if (image.imageUrl) {
      return image.imageUrl;
    }
    return null;
  },

  // Convert blob to data URL (persistent, doesn't require keeping Blob in memory)
  getImageDataUrl: async (image: GalleryImage): Promise<string | null> => {
    if (image.imageBlob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(image.imageBlob!);
      });
    }
    if (image.imageUrl) {
      return image.imageUrl;
    }
    return null;
  },

  // Update SEO metadata for an image
  updateSEO: async (id: string, seoData: ImageSEOData): Promise<GalleryImage> => {
    await ensureInitialized();

    const image = await db.images.get(id);
    if (!image) {
      throw new Error('Image not found');
    }

    const updatedSEO: ImageSEOData = {
      ...seoData,
      analyzedAt: new Date().toISOString(),
    };

    await db.images.update(id, { seo: updatedSEO });
    return { ...image, seo: updatedSEO };
  },

  // Update image description and service type
  updateMetadata: async (
    id: string,
    metadata: { description?: string; serviceType?: string }
  ): Promise<GalleryImage> => {
    await ensureInitialized();

    const image = await db.images.get(id);
    if (!image) {
      throw new Error('Image not found');
    }

    await db.images.update(id, metadata);
    return { ...image, ...metadata };
  },

  // Get image by ID
  getById: async (id: string): Promise<GalleryImage | undefined> => {
    await ensureInitialized();
    return db.images.get(id);
  },

  // Get images without SEO data
  getWithoutSEO: async (): Promise<GalleryImage[]> => {
    await ensureInitialized();
    const all = await db.images.toArray();
    return all.filter(img => !img.seo || !img.seo.analyzedAt);
  },

  // Get images with SEO data
  getWithSEO: async (): Promise<GalleryImage[]> => {
    await ensureInitialized();
    const all = await db.images.toArray();
    return all.filter(img => img.seo && img.seo.analyzedAt);
  },
};

// ==================== BOOKINGS ====================

export const dbBookings = {
  getAll: async (): Promise<Booking[]> => {
    await ensureInitialized();
    return db.bookings.orderBy('createdAt').reverse().toArray();
  },

  add: async (
    booking: Omit<Booking, 'id' | 'createdAt' | 'status'>
  ): Promise<Booking> => {
    await ensureInitialized();

    const newBooking: Booking = {
      ...booking,
      id: generateId(),
      status: 'pending',
      createdAt: new Date().toISOString(),
    };

    await db.bookings.add(newBooking);
    return newBooking;
  },

  update: async (id: string, updates: Partial<Booking>): Promise<Booking> => {
    await ensureInitialized();

    const booking = await db.bookings.get(id);
    if (!booking) {
      throw new Error('Booking not found');
    }

    await db.bookings.update(id, updates);
    return { ...booking, ...updates };
  },

  delete: async (id: string): Promise<void> => {
    await ensureInitialized();
    await db.bookings.delete(id);
  },

  getByStatus: async (status: BookingStatus): Promise<Booking[]> => {
    await ensureInitialized();
    return db.bookings.where('status').equals(status).toArray();
  },

  getByDate: async (date: string): Promise<Booking[]> => {
    await ensureInitialized();
    return db.bookings.where('date').equals(date).toArray();
  },
};

// ==================== BUSINESS HOURS ====================

export const dbBusinessHours = {
  get: async (): Promise<BusinessHoursData> => {
    await ensureInitialized();
    const doc = await db.businessHours.get('singleton');
    return doc?.data || DEFAULT_BUSINESS_HOURS;
  },

  update: async (hours: BusinessHoursData): Promise<BusinessHoursData> => {
    await ensureInitialized();
    await db.businessHours.put({
      id: 'singleton',
      data: hours,
    });
    return hours;
  },
};

// ==================== BACKUP / RESTORE ====================

export const dbBackup = {
  export: async (): Promise<Blob> => {
    await ensureInitialized();
    
    // Update backup version
    const settings = await db.settings.get('singleton');
    await db.settings.put({
      id: 'singleton',
      ...settings,
      backupVersion: (settings?.backupVersion || 0) + 1,
      lastBackup: new Date().toISOString(),
    });

    const blob = await exportDB(db);
    return blob;
  },

  import: async (blob: Blob): Promise<void> => {
    await ensureInitialized();
    
    // Clear existing data
    await db.users.clear();
    await db.bookings.clear();
    await db.images.clear();
    await db.businessHours.clear();
    await db.settings.clear();

    // Import new data
    await importInto(db, blob, {
      clearTablesBeforeImport: true,
      overwriteValues: true,
    });

  },

  downloadBackup: async (): Promise<void> => {
    const blob = await dbBackup.export();
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().split('T')[0];
    const a = document.createElement('a');
    a.href = url;
    a.download = `showgroom-backup-${date}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ==================== UTILITY ====================

export async function clearAllData(): Promise<void> {
  await db.users.clear();
  await db.bookings.clear();
  await db.images.clear();
  await db.businessHours.clear();
  await db.settings.clear();
  await initializeDatabase();
}

export function isUsingDexie(): boolean {
  return true;
}